import {
  REPORT_GENERATION_ENTITIES_BY_ID,
  REPORT_GENERATION_GALLERY_TEMPLATES,
} from './reportGenerationSemanticCatalog';
import {
  YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS,
  YSTNJ_SCHOLARSHIP_WORKSHEET_TEMPLATE_ID,
} from './ystnjScholarshipWorksheetReport';

export type RunReportsVisualizationPrimarySurface = 'visual' | 'table';
export type RunReportsVisualizationChartType = 'bar' | 'line' | 'pie' | 'scatter';
export type RunReportsVisualizationRenderKind = 'grouped-bar' | 'ribbon-line';
export type RunReportsVisualizationAggregate = 'sum' | 'average' | 'count';
export type RunReportsVisualizationBucketPolicy =
  | 'category'
  | 'date-month'
  | 'numeric-bucket'
  | 'numeric-pair';

export type RunReportsTemplateVisualizationMapping = {
  id: string;
  templateId: string;
  useCaseId: string;
  label: string;
  primarySurface: RunReportsVisualizationPrimarySurface;
  fallbackSurface: 'table';
  chartTypes: RunReportsVisualizationChartType[];
  advancedVisuals: RunReportsVisualizationRenderKind[];
  bucketPolicy: RunReportsVisualizationBucketPolicy;
  dimensionFieldIds: string[];
  groupFieldIds: string[];
  metricFieldIds: string[];
  xFieldId?: string;
  yFieldId?: string;
  aggregate: RunReportsVisualizationAggregate;
  requiredBucketFields: string[];
  requiredMetricFields: string[];
  visualEnrichmentFieldIds: string[];
  unsafeLabelFieldIds: string[];
  minBuckets: number;
  minSeries: number;
  assistantQuickActionIds: string[];
};

const PII_LABEL_FIELD_IDS = [
  'family_name',
  'primary_contact',
  'email',
  'phone',
  'address',
  'first_name',
  'last_name',
  'reference_number',
  'fa_ref_number',
  'scholarship_name',
  'description',
  'doc_name',
] as const;

export const RUN_REPORTS_UNSAFE_CHART_LABEL_FIELD_IDS: string[] = [...PII_LABEL_FIELD_IDS];

const visual = (
  mapping: Omit<
    RunReportsTemplateVisualizationMapping,
    'fallbackSurface' | 'unsafeLabelFieldIds' | 'assistantQuickActionIds'
  > & {
    unsafeLabelFieldIds?: string[];
    assistantQuickActionIds?: string[];
  },
): RunReportsTemplateVisualizationMapping => ({
  ...mapping,
  fallbackSurface: 'table',
  unsafeLabelFieldIds: mapping.unsafeLabelFieldIds ?? [],
  assistantQuickActionIds: mapping.assistantQuickActionIds ?? ['run-reports-show-best-visual'],
});

export const RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS: RunReportsTemplateVisualizationMapping[] = [
  visual({
    id: 'template-visual-aging-report',
    templateId: 'aging-report',
    useCaseId: 'aging-pressure',
    label: 'Aging pressure by due month and academic year',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'date-month',
    dimensionFieldIds: ['due_date'],
    groupFieldIds: ['academic_year', 'is_overdue'],
    metricFieldIds: ['amount_due', 'balance_remaining'],
    aggregate: 'sum',
    requiredBucketFields: ['due_date', 'academic_year'],
    requiredMetricFields: ['amount_due', 'balance_remaining'],
    visualEnrichmentFieldIds: ['is_overdue'],
    unsafeLabelFieldIds: ['family_name'],
    minBuckets: 2,
    minSeries: 2,
    assistantQuickActionIds: [
      'run-reports-build-from-aging-report',
      'run-reports-add-collection-risk-filters',
      'run-reports-show-best-visual',
    ],
  }),
  visual({
    id: 'template-visual-collection-summary',
    templateId: 'collection-summary',
    useCaseId: 'collections-mix',
    label: 'Collections by month and payment method',
    primarySurface: 'visual',
    chartTypes: ['bar', 'pie'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'date-month',
    dimensionFieldIds: ['payment_date'],
    groupFieldIds: ['payment_method'],
    metricFieldIds: ['payment_amount'],
    aggregate: 'sum',
    requiredBucketFields: ['payment_date', 'payment_method'],
    requiredMetricFields: ['payment_amount'],
    visualEnrichmentFieldIds: [],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-payment-history',
    templateId: 'payment-history',
    useCaseId: 'payment-history-cashflow',
    label: 'Payment history by method and posting status',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'date-month',
    dimensionFieldIds: ['payment_date'],
    groupFieldIds: ['payment_method', 'payment_status_p'],
    metricFieldIds: ['payment_amount'],
    aggregate: 'sum',
    requiredBucketFields: ['payment_date', 'payment_method'],
    requiredMetricFields: ['payment_amount'],
    visualEnrichmentFieldIds: ['payment_status_p'],
    unsafeLabelFieldIds: ['family_name', 'reference_number'],
    minBuckets: 2,
    minSeries: 2,
    assistantQuickActionIds: ['run-reports-show-best-visual', 'run-reports-choose-columns'],
  }),
  visual({
    id: 'template-visual-outstanding-balances',
    templateId: 'outstanding-balances',
    useCaseId: 'outstanding-balance-risk',
    label: 'Outstanding balance risk by account status',
    primarySurface: 'visual',
    chartTypes: ['bar', 'scatter'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['account_status'],
    groupFieldIds: [],
    metricFieldIds: ['account_balance', 'annual_tuition'],
    xFieldId: 'annual_tuition',
    yFieldId: 'account_balance',
    aggregate: 'sum',
    requiredBucketFields: ['account_status'],
    requiredMetricFields: ['account_balance'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['family_name'],
    minBuckets: 2,
    minSeries: 1,
    assistantQuickActionIds: ['run-reports-add-collection-risk-filters', 'run-reports-show-best-visual'],
  }),
  visual({
    id: 'template-visual-tuition-ledger-review',
    templateId: 'tuition-ledger-review',
    useCaseId: 'tuition-cashflow',
    label: 'Tuition cashflow by entry month and entry type',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'date-month',
    dimensionFieldIds: ['entry_date'],
    groupFieldIds: ['entry_type'],
    metricFieldIds: ['debit', 'credit'],
    aggregate: 'sum',
    requiredBucketFields: ['entry_date', 'entry_type'],
    requiredMetricFields: ['debit', 'credit'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['family_name', 'description'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-contract-status',
    templateId: 'contract-status',
    useCaseId: 'contract-value',
    label: 'Contract value by status and academic year',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['contract_status'],
    groupFieldIds: ['contract_year'],
    metricFieldIds: ['contract_amount'],
    aggregate: 'sum',
    requiredBucketFields: ['contract_status', 'contract_year'],
    requiredMetricFields: ['contract_amount'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['family_name'],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-apps-by-status',
    templateId: 'apps-by-status',
    useCaseId: 'fa-application-status-flow',
    label: 'Financial aid applications by status and pending time',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['fa_status'],
    groupFieldIds: [],
    metricFieldIds: ['days_pending'],
    aggregate: 'average',
    requiredBucketFields: ['fa_status'],
    requiredMetricFields: ['days_pending'],
    visualEnrichmentFieldIds: ['fa_submission_date'],
    unsafeLabelFieldIds: ['family_name', 'fa_ref_number'],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-awards-by-grade',
    templateId: 'awards-by-grade',
    useCaseId: 'award-by-grade',
    label: 'Award dollars by grade level',
    primarySurface: 'visual',
    chartTypes: ['bar'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['grade_level'],
    groupFieldIds: ['award_year', 'award_status'],
    metricFieldIds: ['award_amount'],
    aggregate: 'sum',
    requiredBucketFields: ['grade_level'],
    requiredMetricFields: ['award_amount'],
    visualEnrichmentFieldIds: ['award_year', 'award_status'],
    unsafeLabelFieldIds: ['first_name', 'last_name', 'scholarship_name'],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-total-committed',
    templateId: 'total-committed',
    useCaseId: 'aid-commitment-status',
    label: 'Committed aid dollars by year and award status',
    primarySurface: 'visual',
    chartTypes: ['bar', 'pie'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['award_year'],
    groupFieldIds: ['award_status'],
    metricFieldIds: ['award_amount'],
    aggregate: 'sum',
    requiredBucketFields: ['award_year', 'award_status'],
    requiredMetricFields: ['award_amount'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['scholarship_name'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-avg-by-income',
    templateId: 'avg-by-income',
    useCaseId: 'reported_income-bucket',
    label: 'Average aid recommendation by income bracket',
    primarySurface: 'visual',
    chartTypes: ['bar', 'scatter'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'numeric-bucket',
    dimensionFieldIds: ['reported_income'],
    groupFieldIds: [],
    metricFieldIds: ['system_rec_pct', 'award_pct'],
    xFieldId: 'reported_income',
    yFieldId: 'award_pct',
    aggregate: 'average',
    requiredBucketFields: ['reported_income'],
    requiredMetricFields: ['system_rec_pct', 'award_pct'],
    visualEnrichmentFieldIds: [],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-processing-time',
    templateId: 'processing-time',
    useCaseId: 'fa-processing-time',
    label: 'Processing time by submission month and status',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'date-month',
    dimensionFieldIds: ['fa_submission_date'],
    groupFieldIds: ['fa_status'],
    metricFieldIds: ['days_pending'],
    aggregate: 'average',
    requiredBucketFields: ['fa_submission_date', 'fa_status'],
    requiredMetricFields: ['days_pending'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['fa_ref_number'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-doc-compliance',
    templateId: 'doc-compliance',
    useCaseId: 'document-compliance-rate',
    label: 'Document compliance by aid status',
    primarySurface: 'visual',
    chartTypes: ['bar', 'pie'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['doc_status'],
    groupFieldIds: ['fa_status'],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: ['doc_status', 'fa_status'],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['fa_ref_number'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-override-frequency',
    templateId: 'override-frequency',
    useCaseId: 'override-frequency',
    label: 'Override amount against system recommendation',
    primarySurface: 'visual',
    chartTypes: ['scatter', 'bar'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'numeric-pair',
    dimensionFieldIds: ['confidence_level'],
    groupFieldIds: ['confidence_level'],
    metricFieldIds: ['override_amount', 'system_rec_pct'],
    xFieldId: 'system_rec_pct',
    yFieldId: 'override_amount',
    aggregate: 'average',
    requiredBucketFields: ['confidence_level'],
    requiredMetricFields: ['system_rec_pct', 'override_amount'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['fa_ref_number'],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-appeal-outcomes',
    templateId: 'appeal-outcomes',
    useCaseId: 'appeal-outcome-impact',
    label: 'Appeal outcomes by income bracket',
    primarySurface: 'visual',
    chartTypes: ['bar', 'scatter'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'numeric-bucket',
    dimensionFieldIds: ['reported_income'],
    groupFieldIds: ['fa_status'],
    metricFieldIds: ['override_amount'],
    xFieldId: 'reported_income',
    yFieldId: 'override_amount',
    aggregate: 'average',
    requiredBucketFields: ['reported_income', 'fa_status'],
    requiredMetricFields: ['override_amount'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['fa_ref_number'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-app-pipeline',
    templateId: 'app-pipeline',
    useCaseId: 'admissions-pipeline',
    label: 'Admissions pipeline by stage and entry year',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['app_status'],
    groupFieldIds: ['entry_year'],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: ['app_status', 'entry_year'],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: [],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-enrollment-conversion',
    templateId: 'enrollment-conversion',
    useCaseId: 'enrollment-conversion',
    label: 'Enrollment conversion by grade and confirmation state',
    primarySurface: 'visual',
    chartTypes: ['bar'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['applying_grade'],
    groupFieldIds: ['enrollment_confirmed', 'app_status'],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: ['applying_grade', 'enrollment_confirmed'],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: ['app_status'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-incomplete-docs',
    templateId: 'incomplete-docs',
    useCaseId: 'incomplete-docs-risk',
    label: 'Incomplete documents by application status',
    primarySurface: 'visual',
    chartTypes: ['bar'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['app_status'],
    groupFieldIds: ['docs_complete'],
    metricFieldIds: ['missing_docs'],
    aggregate: 'sum',
    requiredBucketFields: ['app_status', 'docs_complete'],
    requiredMetricFields: ['missing_docs'],
    visualEnrichmentFieldIds: [],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-demographics',
    templateId: 'demographics',
    useCaseId: 'student-demographics',
    label: 'Student demographics by grade and division',
    primarySurface: 'visual',
    chartTypes: ['bar', 'pie'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['grade_level'],
    groupFieldIds: ['division', 'student_status'],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: ['grade_level', 'division'],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: ['student_status'],
    unsafeLabelFieldIds: ['first_name', 'last_name'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-enrollment-by-grade',
    templateId: 'enrollment-by-grade',
    useCaseId: 'enrollment-by-grade',
    label: 'Enrollment by grade, division, and status',
    primarySurface: 'visual',
    chartTypes: ['bar', 'line'],
    advancedVisuals: ['grouped-bar', 'ribbon-line'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['grade_level'],
    groupFieldIds: ['division', 'student_status'],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: ['grade_level', 'division'],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: ['start_date', 'student_status'],
    minBuckets: 2,
    minSeries: 2,
  }),
  visual({
    id: 'template-visual-contact-directory',
    templateId: 'contact-directory',
    useCaseId: 'contact-directory-table',
    label: 'Contact directory table and family graph',
    primarySurface: 'table',
    chartTypes: [],
    advancedVisuals: [],
    bucketPolicy: 'category',
    dimensionFieldIds: [],
    groupFieldIds: [],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: [],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['family_name', 'primary_contact', 'email', 'phone', 'address'],
    minBuckets: 0,
    minSeries: 0,
    assistantQuickActionIds: ['run-reports-choose-columns'],
  }),
  visual({
    id: 'template-visual-ystnj-scholarship-worksheet',
    templateId: YSTNJ_SCHOLARSHIP_WORKSHEET_TEMPLATE_ID,
    useCaseId: 'ystnj-scholarship-worksheet-export',
    label: 'YSTNJ scholarship worksheet export table',
    primarySurface: 'table',
    chartTypes: [],
    advancedVisuals: [],
    bucketPolicy: 'category',
    dimensionFieldIds: [],
    groupFieldIds: [],
    metricFieldIds: [
      YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.contractAmount,
      YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedAmount,
      YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardAmount,
    ],
    aggregate: 'sum',
    requiredBucketFields: [],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName],
    minBuckets: 0,
    minSeries: 0,
    assistantQuickActionIds: ['run-reports-export'],
  }),
  visual({
    id: 'template-visual-fa-tuition-recon',
    templateId: 'fa-tuition-recon',
    useCaseId: 'fa-tuition-reconciliation',
    label: 'Aid awards against tuition balance',
    primarySurface: 'visual',
    chartTypes: ['scatter', 'bar'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'numeric-pair',
    dimensionFieldIds: [],
    groupFieldIds: [],
    metricFieldIds: ['award_amount', 'annual_tuition', 'account_balance'],
    xFieldId: 'award_amount',
    yFieldId: 'account_balance',
    aggregate: 'sum',
    requiredBucketFields: [],
    requiredMetricFields: ['award_amount', 'account_balance'],
    visualEnrichmentFieldIds: [],
    unsafeLabelFieldIds: ['family_name'],
    minBuckets: 2,
    minSeries: 1,
  }),
  visual({
    id: 'template-visual-new-family-onboarding',
    templateId: 'new-family-onboarding',
    useCaseId: 'new-family-onboarding',
    label: 'New family onboarding by application and contract state',
    primarySurface: 'visual',
    chartTypes: ['bar'],
    advancedVisuals: ['grouped-bar'],
    bucketPolicy: 'category',
    dimensionFieldIds: ['app_status'],
    groupFieldIds: ['contract_status', 'enrollment_status'],
    metricFieldIds: [],
    aggregate: 'count',
    requiredBucketFields: ['app_status', 'contract_status'],
    requiredMetricFields: [],
    visualEnrichmentFieldIds: ['enrollment_status'],
    unsafeLabelFieldIds: ['family_name'],
    minBuckets: 2,
    minSeries: 2,
  }),
];

export const RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS_BY_TEMPLATE_ID = Object.freeze(
  Object.fromEntries(
    RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS.map((mapping) => [mapping.templateId, mapping]),
  ),
) as Readonly<Record<string, RunReportsTemplateVisualizationMapping>>;

export const resolveRunReportsVisualizationMappingByTemplateId = (
  templateId: string | null | undefined,
): RunReportsTemplateVisualizationMapping | null => {
  if (!templateId) {
    return null;
  }
  return RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS_BY_TEMPLATE_ID[templateId] ?? null;
};

export const inferRunReportsVisualizationMappingFromFields = (
  fieldIds: readonly string[],
): RunReportsTemplateVisualizationMapping | null => {
  const selected = new Set(fieldIds);
  const matches = RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS
    .map((mapping, index) => {
      const template = REPORT_GENERATION_GALLERY_TEMPLATES.find(
        (candidate) => candidate.id === mapping.templateId,
      );
      const templateFields = template?.fields ?? [];
      const hasExactTemplateSignature =
        templateFields.length > 0 &&
        templateFields.every((fieldId) => selected.has(fieldId));
      const exactFieldHits = templateFields.filter((fieldId) => selected.has(fieldId)).length;
      const mappedFieldHits = [
        ...mapping.dimensionFieldIds,
        ...mapping.groupFieldIds,
        ...mapping.metricFieldIds,
      ].filter((fieldId) => selected.has(fieldId)).length;
      return {
        mapping,
        index,
        hasExactTemplateSignature,
        score: exactFieldHits * 10 + mappedFieldHits,
        misses: templateFields.filter((fieldId) => !selected.has(fieldId)).length,
      };
    })
    .filter((entry) => entry.hasExactTemplateSignature)
    .sort((left, right) =>
      right.score - left.score ||
      left.misses - right.misses ||
      left.index - right.index,
    );
  return matches[0]?.mapping ?? null;
};

const knownFieldIdsForTemplate = (templateId: string): Set<string> => {
  const template = REPORT_GENERATION_GALLERY_TEMPLATES.find((entry) => entry.id === templateId);
  if (!template) {
    return new Set();
  }
  return new Set(
    template.entities.flatMap((entityId) =>
      REPORT_GENERATION_ENTITIES_BY_ID[entityId]?.categories.flatMap((category) =>
        category.fields.map((field) => field.id),
      ) ?? [],
    ),
  );
};

export type RunReportsVisualizationMappingValidationIssue = {
  severity: 'error' | 'warning';
  code: string;
  message: string;
};

export const validateRunReportsVisualizationMappings = (): RunReportsVisualizationMappingValidationIssue[] => {
  const issues: RunReportsVisualizationMappingValidationIssue[] = [];
  const templateIds = new Set(REPORT_GENERATION_GALLERY_TEMPLATES.map((template) => template.id));
  const mappingIds = new Set<string>();
  const mappedTemplateIds = new Set<string>();

  for (const mapping of RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS) {
    if (mappingIds.has(mapping.id)) {
      issues.push({
        severity: 'error',
        code: 'duplicate-visualization-mapping-id',
        message: `${mapping.id} is declared more than once.`,
      });
    }
    mappingIds.add(mapping.id);
    if (!templateIds.has(mapping.templateId)) {
      issues.push({
        severity: 'error',
        code: 'unknown-visualization-template',
        message: `${mapping.id} references unknown template ${mapping.templateId}.`,
      });
      continue;
    }
    mappedTemplateIds.add(mapping.templateId);

    const knownFieldIds = knownFieldIdsForTemplate(mapping.templateId);
    const referencedFieldIds = [
      ...mapping.dimensionFieldIds,
      ...mapping.groupFieldIds,
      ...mapping.metricFieldIds,
      ...mapping.requiredBucketFields,
      ...mapping.requiredMetricFields,
      ...mapping.visualEnrichmentFieldIds,
      mapping.xFieldId,
      mapping.yFieldId,
    ].filter((fieldId): fieldId is string => Boolean(fieldId));
    const unknownFieldIds = referencedFieldIds.filter((fieldId) => !knownFieldIds.has(fieldId));
    if (unknownFieldIds.length > 0) {
      issues.push({
        severity: 'error',
        code: 'visualization-field-unknown',
        message: `${mapping.id} references fields outside template entities: ${[...new Set(unknownFieldIds)].join(', ')}.`,
      });
    }

    const unsafeBucketFields = [...mapping.dimensionFieldIds, ...mapping.groupFieldIds]
      .filter((fieldId) => RUN_REPORTS_UNSAFE_CHART_LABEL_FIELD_IDS.includes(fieldId));
    if (unsafeBucketFields.length > 0) {
      issues.push({
        severity: 'error',
        code: 'unsafe-visualization-label-field',
        message: `${mapping.id} uses unsafe label fields as buckets: ${[...new Set(unsafeBucketFields)].join(', ')}.`,
      });
    }

    if (mapping.primarySurface === 'visual' && mapping.chartTypes.length === 0) {
      issues.push({
        severity: 'error',
        code: 'visualization-missing-chart-type',
        message: `${mapping.id} is visual-first but declares no chart types.`,
      });
    }
  }

  for (const templateId of templateIds) {
    if (!mappedTemplateIds.has(templateId)) {
      issues.push({
        severity: 'error',
        code: 'missing-template-visualization-mapping',
        message: `Missing Run Reports visualization mapping for ${templateId}.`,
      });
    }
  }

  return issues;
};
